/** SRO desk reports (information only — never for a charge) and school safety concerns. */
import { useState } from 'react';
import { SAFETY_PLACES, SAFETY_STATUS_LABEL } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, EmptyState, fileBase64, when } from './common.tsx';
import { FileList, UploadButton } from './WorkFiles.tsx';

const WARNING = 'Information only. A desk report is for things you want to remember that don’t rise to an incident report. It is not for making a charge — if a charge may come of it, write an incident report.';

function useSchools() {
  return useLoad(() => api.get<any>('/api/schools').then((r) => r.schools as { id: string; name: string; mine: boolean }[]));
}

// ─── Desk reports ───────────────────────────────────────────────────────────
export function DeskReportsPage() {
  const { navigate } = useRouter();
  const [school, setSchool] = useState('');
  const [q, setQ] = useState('');
  const schools = useSchools();
  const r = useLoad(() => api.get<any>(`/api/sro/desk?school=${school}&q=${encodeURIComponent(q)}`), [school, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Schools"
        title="Desk reports"
        sub="Only SROs and their supervisors see these."
        actions={
          r.data?.canWrite && (
            <button className="btn primary" onClick={() => navigate(at('/schools/desk/new'))}>
              New desk report
            </button>
          )
        }
      />
      <Notice tone="warn">{WARNING}</Notice>
      <div className="row gap-sm wrap">
        <select value={school} onChange={(e) => setSchool(e.target.value)} aria-label="School" style={{ maxWidth: 260 }}>
          <option value="">All schools</option>
          {(schools.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <input type="search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search desk reports" style={{ maxWidth: 240 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.reports.length ? (
        <EmptyState icon="clipboard" title="No desk reports" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>School</th>
                <th>What</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {r.data.reports.map((d: any) => (
                <tr key={d.id} className="clickable" onClick={() => navigate(at(`/schools/desk/${d.id}`))}>
                  <td>
                    {when(d.happened_at)}
                    <div className="small muted mono">{d.number}</div>
                  </td>
                  <td>{d.school_name ?? '—'}</td>
                  <td className="wrap">
                    {d.what.slice(0, 120)}
                    {d.what.length > 120 ? '…' : ''}
                    {d.case_number && (
                      <div>
                        <Badge tone="info">Now incident report {d.case_number}</Badge>
                      </div>
                    )}
                  </td>
                  <td>{d.by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const toLocal = (d: Date) => {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 16);
};

export function NewDeskReport() {
  const { navigate } = useRouter();
  const schools = useSchools();
  const [f, setF] = useState<Record<string, string>>({ happenedAt: toLocal(new Date()) });
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="Desk reports" title="New desk report" />
      <Notice tone="warn">
        <strong>{WARNING}</strong>
      </Notice>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => navigate(at(`/schools/desk/${(await api.post<{ id: string }>('/api/sro/desk', { ...f, happenedAt: new Date(f.happenedAt!).toISOString(), schoolId: f.schoolId || null })).id}`), true));
        }}
      >
        <Card title="What you want to remember">
          <div className="grid-3">
            <label>
              School
              <select value={f.schoolId ?? ''} onChange={(e) => setF({ ...f, schoolId: e.target.value })}>
                <option value="">—</option>
                {(schools.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              When
              <input type="datetime-local" value={f.happenedAt} onChange={(e) => setF({ ...f, happenedAt: e.target.value })} />
            </label>
            <label>
              Who (students, staff, parents)
              <input value={f.people ?? ''} onChange={(e) => setF({ ...f, people: e.target.value })} />
            </label>
          </div>
          <label>
            What happened
            <textarea rows={6} required value={f.what ?? ''} onChange={(e) => setF({ ...f, what: e.target.value })} />
          </label>
          <label>
            Follow-up
            <input value={f.followUp ?? ''} onChange={(e) => setF({ ...f, followUp: e.target.value })} placeholder="Check back with the counselor Friday" />
          </label>
        </Card>
        <ErrorBox error={a.error} />
        <div>
          <button className="btn primary" disabled={a.busy}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export function DeskReportDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/sro/desk/${id}`), [id]);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { report: d, files, canMakeReport } = r.data;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Desk reports"
        title={`${d.number}${d.school_name ? ` · ${d.school_name}` : ''}`}
        sub={`${when(d.happened_at)} · ${d.by_name}`}
        actions={
          d.incident_id ? (
            <button className="btn" onClick={() => navigate(at(`/reports/${d.incident_id}`))}>
              Open incident report {d.case_number}
            </button>
          ) : (
            canMakeReport && (
              <button
                className="btn primary"
                disabled={a.busy}
                onClick={() => window.confirm('Start an incident report from this desk report? The desk report stays as it is.') && a.run(async () => navigate(at(`/reports/${(await api.post<{ id: string }>(`/api/sro/desk/${id}/make-report`)).id}`)))}
              >
                Make this an incident report
              </button>
            )
          )
        }
      />
      <Notice tone="warn">{WARNING}</Notice>
      <ErrorBox error={a.error} />
      <Card title="What happened">
        <p className="pre">{d.what}</p>
        <dl className="kv">
          <dt>Who</dt>
          <dd>{d.people ?? '—'}</dd>
          <dt>Follow-up</dt>
          <dd>{d.follow_up ?? '—'}</dd>
        </dl>
      </Card>
      <Card title="Files and photos" actions={<UploadButton url={`/api/sro/desk/${id}/files`} onDone={r.reload} />}>
        <FileList files={files} />
      </Card>
    </div>
  );
}

// ─── Safety concerns ────────────────────────────────────────────────────────
const STONE: Record<string, 'bad' | 'warn' | 'ok'> = { open: 'bad', reported: 'warn', fixed: 'ok' };

export function SafetyPage() {
  const { navigate } = useRouter();
  const [school, setSchool] = useState('');
  const [status, setStatus] = useState('open');
  const schools = useSchools();
  const r = useLoad(() => api.get<any>(`/api/sro/safety?school=${school}&status=${status}`), [school, status]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Schools"
        title="Safety concerns"
        sub="Problems at a school — a door that doesn’t latch, a broken camera — from open to reported to fixed."
        actions={
          r.data?.canWrite && (
            <button className="btn primary" onClick={() => navigate(at('/schools/safety/new'))}>
              Report a concern
            </button>
          )
        }
      />
      <div className="row gap-sm wrap">
        <select value={school} onChange={(e) => setSchool(e.target.value)} aria-label="School" style={{ maxWidth: 260 }}>
          <option value="">All schools</option>
          {(schools.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="seg-ctl" role="tablist" aria-label="Status">
          {(
            [
              ['open', 'Not fixed'],
              ['fixed', 'Fixed'],
              ['all', 'All'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.concerns.length ? (
        <EmptyState icon="check" title="No safety concerns" />
      ) : (
        <div className="bolo-grid">
          {r.data.concerns.map((c: any) => (
            <button key={c.id} type="button" className="card bolo-card" onClick={() => navigate(at(`/schools/safety/${c.id}`))}>
              {c.photo ? <img src={`/api/work-files/${c.photo}`} alt="" /> : <div className="bolo-ph">⚠</div>}
              <div className="stack xs">
                <div className="row between gap-sm">
                  <strong>{c.school_name}</strong>
                  <Badge tone={STONE[c.status]} dot>
                    {SAFETY_STATUS_LABEL[c.status as keyof typeof SAFETY_STATUS_LABEL]}
                  </Badge>
                </div>
                <div className="small">
                  {c.place_kind}
                  {c.place ? ` — ${c.place}` : ''}
                </div>
                <div className="small">{c.problem}</div>
                <div className="small muted">
                  {c.number} · {c.by_name} · {when(c.created_at)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function NewSafety() {
  const { navigate } = useRouter();
  const schools = useSchools();
  const [f, setF] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const a = useAction();
  const mine = (schools.data ?? []).filter((s) => s.mine);
  return (
    <div className="ops stack">
      <PageHeader crumb="Safety concerns" title="Report a safety concern" />
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ id: string }>('/api/sro/safety', { ...f, schoolId: f.schoolId ?? (mine.length === 1 ? mine[0]!.id : undefined) });
            for (const p of photos) await api.post(`/api/sro/safety/${r.id}/files`, { name: p.name, dataBase64: await fileBase64(p) });
            navigate(at(`/schools/safety/${r.id}`), true);
          });
        }}
      >
        <Card title="The problem">
          <div className="grid-3">
            <label>
              School
              <select required value={f.schoolId ?? (mine.length === 1 ? mine[0]!.id : '')} onChange={(e) => setF({ ...f, schoolId: e.target.value })}>
                <option value="">—</option>
                {(schools.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Location
              <select required value={f.placeKind ?? ''} onChange={(e) => setF({ ...f, placeKind: e.target.value })}>
                <option value="">—</option>
                {SAFETY_PLACES.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </label>
            <label>
              Which one (room #, hallway, door)
              <input value={f.place ?? ''} onChange={(e) => setF({ ...f, place: e.target.value })} />
            </label>
          </div>
          <label>
            Description of the problem
            <textarea rows={4} required value={f.problem ?? ''} onChange={(e) => setF({ ...f, problem: e.target.value })} />
          </label>
          <label>
            Notes
            <input value={f.note ?? ''} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </label>
          <label>
            Photos (take or upload)
            <input type="file" accept="image/*" multiple onChange={(e) => setPhotos(Array.from(e.target.files ?? []))} />
          </label>
          {photos.length > 0 && <p className="small muted">{photos.length} photo(s) will be added.</p>}
        </Card>
        <ErrorBox error={a.error} />
        <div>
          <button className="btn primary" disabled={a.busy}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export function SafetyDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/sro/safety/${id}`), [id]);
  const a = useAction();
  const [note, setNote] = useState('');
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { concern: c, files, canEdit } = r.data;
  const save = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/sro/safety/${id}`, body), setNote(''), r.reload()));
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Safety concerns"
        title={`${c.school_name} · ${c.place_kind}${c.place ? ` — ${c.place}` : ''}`}
        sub={`${c.number} · ${c.by_name} · ${when(c.created_at)}`}
        actions={
          <Badge tone={STONE[c.status]} dot>
            {SAFETY_STATUS_LABEL[c.status as keyof typeof SAFETY_STATUS_LABEL]}
          </Badge>
        }
      />
      <ErrorBox error={a.error} />
      <div className="grid-2-cards">
        <Card title="The problem">
          <p className="pre">{c.problem}</p>
          {canEdit && (
            <div className="stack sm">
              <label>
                Add a note
                <input value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
              <div className="row gap-sm wrap">
                {c.status === 'open' && (
                  <button className="btn" disabled={a.busy} onClick={() => save({ status: 'reported', note: note || undefined })}>
                    Reported to the school
                  </button>
                )}
                {c.status !== 'fixed' && (
                  <button className="btn primary" disabled={a.busy} onClick={() => save({ status: 'fixed', note: note || undefined })}>
                    Fixed
                  </button>
                )}
                {c.status === 'fixed' && (
                  <button className="btn ghost" disabled={a.busy} onClick={() => save({ status: 'open', note: note || undefined })}>
                    Reopen
                  </button>
                )}
                {note && (
                  <button className="btn ghost" disabled={a.busy} onClick={() => save({ note })}>
                    Save note
                  </button>
                )}
              </div>
            </div>
          )}
        </Card>
        <Card title="Notes">
          {c.notes.length ? (
            <ul className="timeline">
              {c.notes.map((n: any, i: number) => (
                <li key={i}>
                  <strong>{n.text}</strong> <span className="muted small">{when(n.at)} · {n.by}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">No notes yet.</p>
          )}
        </Card>
      </div>
      <Card title="Photos" actions={canEdit && <UploadButton url={`/api/sro/safety/${id}/files`} onDone={r.reload} label="Add photos" accept="image/*" />}>
        <FileList files={files} />
      </Card>
    </div>
  );
}
